/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Button } from '@/components/buttons';

export interface LivePersonLauncherProps {
    available: boolean;
    hidden?: boolean;
    onOpen: () => void;
}

/**
 * Ashley-owned launcher shown after LivePerson renders its overlay.
 * The extra mobile bottom offset avoids sticky cart/checkout controls and
 * respects the device safe area.
 */
export default function LivePersonLauncher({ available, hidden = false, onOpen }: LivePersonLauncherProps) {
    if (!available || hidden) return null;

    return (
        <Button
            type="button"
            onClick={onOpen}
            aria-label="Chat With Us"
            className="fixed right-4 bottom-[calc(env(safe-area-inset-bottom)+5rem+var(--ot-banner-height,0px))] z-40 h-12 rounded-full px-5 shadow-lg lg:right-6 lg:bottom-[calc(1.5rem+var(--ot-banner-height,0px))]"
            data-testid="live-person-launcher">
            <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="size-5">
                <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
            </svg>
            <span>Chat With Us</span>
        </Button>
    );
}
